import { basename, isAbsoluteFolderPath, normalizePath, resolveToAbsolutePath } from '../../../utils/path';
import { estimateImageDataSizeBytes, readPngDimensions } from './AssetMemoryParsers';
import type {
    AssetMemoryAnalysis,
    AssetMemoryAssetReference,
    AssetMemoryEstimateOptions,
    AssetMemorySizeEstimate,
    AssetMemorySizeSource
} from './AssetMemoryTypes';

/**
 * Collects unique assets from an initial report so compound-target assets are estimated once.
 */
export const getUniqueAssetReferences = (analysis: AssetMemoryAnalysis): AssetMemoryAssetReference[] => {
    const assetsByKey: Map<string, AssetMemoryAssetReference> = new Map();

    for (const target of analysis.targets) {
        for (const region of target.memoryRegions) {
            for (const asset of region.assets) {
                const key: string = getAssetReferenceKey(asset);
                if (!assetsByKey.has(key)) assetsByKey.set(key, asset);
            }
        }
    }

    return Array.from(assetsByKey.values());
};

/**
 * Builds a stable identity for an asset reference across repeated analysis passes.
 * Fields that affect size or assignment are included so distinct block usages do not collide.
 */
export const getAssetReferenceKey = (asset: AssetMemoryAssetReference): string =>
    [
        asset.blockName,
        asset.tagName,
        asset.name,
        asset.sourcePath,
        asset.memoryName ?? '',
        asset.targetNames.join('|')
    ].join('\u0000');

/**
 * Estimates one asset reference using the same rules that the report builder applies in bulk.
 * Images and fonts differ enough that this function only dispatches to the type-specific estimators.
 */
export const estimateAssetMemorySize = async (
    asset: AssetMemoryAssetReference,
    options: AssetMemoryEstimateOptions
): Promise<AssetMemorySizeEstimate> => {
    if (asset.blockName === 'images') {
        if (asset.tagName === 'file') return estimateSourceFileSize(asset, options, 'Image file');
        return estimateImageDataAssetSize(asset, options);
    }

    return estimateFontAssetSize(asset, options);
};

/**
 * Reads an asset source file and uses its byte length as the resolved size.
 */
const estimateSourceFileSize = async (
    asset: AssetMemoryAssetReference,
    options: AssetMemoryEstimateOptions,
    label: string
): Promise<AssetMemorySizeEstimate> => {
    const data: Uint8Array | null = await readProjectBinaryFile(options, asset.sourcePath);

    if (!data) {
        return {
            sizeBytes: null,
            status: 'unknown',
            note: `${label} source file is not available.`
        };
    }

    return {
        sizeBytes: data.byteLength,
        status: 'resolved',
        source: 'source-file'
    };
};

/**
 * Estimates <images><data> size from generated output when requested, otherwise from PNG dimensions and color format.
 */
const estimateImageDataAssetSize = async (
    asset: AssetMemoryAssetReference,
    options: AssetMemoryEstimateOptions
): Promise<AssetMemorySizeEstimate> => {
    const generatedEstimate: AssetMemorySizeEstimate | null = options.preferGeneratedOutput
        ? await estimateGeneratedOutputSize(asset, options)
        : null;

    if (generatedEstimate?.status === 'resolved') return generatedEstimate;

    const data: Uint8Array | null = await readProjectBinaryFile(options, asset.sourcePath);
    if (!data) {
        return {
            sizeBytes: null,
            status: 'unknown',
            note: 'Image source file is not available.'
        };
    }

    const dimensions = readPngDimensions(data);
    if (!dimensions) {
        return {
            sizeBytes: null,
            status: 'unknown',
            note: 'Image source is not a readable PNG.'
        };
    }

    const estimatedSizeBytes: number | null = estimateImageDataSizeBytes(
        dimensions.width,
        dimensions.height,
        asset.attributes.color_format
    );

    if (estimatedSizeBytes === null) {
        return {
            sizeBytes: null,
            status: 'unknown',
            note: `Cannot estimate image data size for color format "${asset.attributes.color_format ?? ''}".`
        };
    }

    return {
        sizeBytes: estimatedSizeBytes,
        status: 'resolved',
        source: 'png-dimensions',
        note: `${dimensions.width}x${dimensions.height} ${asset.attributes.color_format}`
    };
};

/**
 * Estimates font size from generated output when available.
 * TinyTTF data mode embeds the raw source bytes, so the source file is an exact fallback.
 * Converted bin fonts without generated output are marked as requiring code export for an exact byte count.
 */
const estimateFontAssetSize = async (
    asset: AssetMemoryAssetReference,
    options: AssetMemoryEstimateOptions
): Promise<AssetMemorySizeEstimate> => {
    const asFile: boolean = asset.attributes.as_file?.toLowerCase() === 'true';

    if (asset.tagName === 'tiny_ttf') {
        if (asFile) return estimateSourceFileSize(asset, options, 'Font');

        const generatedEstimate: AssetMemorySizeEstimate | null = await estimateGeneratedOutputSize(asset, options);
        if (generatedEstimate?.status === 'resolved') return generatedEstimate;
        return estimateSourceFileSize(asset, options, 'Font');
    }

    if (asFile) {
        const generatedEstimate: AssetMemorySizeEstimate | null = await estimateGeneratedOutputSize(asset, options);
        if (generatedEstimate?.status === 'resolved') return generatedEstimate;
        return estimateSourceFileSize(asset, options, 'Font');
    }

    const generatedEstimate: AssetMemorySizeEstimate | null = await estimateGeneratedOutputSize(asset, options);
    if (generatedEstimate?.status === 'resolved') return generatedEstimate;

    return {
        sizeBytes: null,
        status: 'requires-code-export',
        note: 'Requires code export for exact byte count.'
    };
};

/**
 * Reads the expected generated asset output and derives the asset payload size.
 * Generated C files are parsed for uint8_t/char array entries; binary outputs use file byte length.
 */
const estimateGeneratedOutputSize = async (
    asset: AssetMemoryAssetReference,
    options: AssetMemoryEstimateOptions
): Promise<AssetMemorySizeEstimate | null> => {
    const generatedPath: string | null = options.getGeneratedAssetPath?.(asset) ?? getDefaultGeneratedAssetPath(asset);
    if (!generatedPath) return null;

    const data: Uint8Array | null = await readProjectBinaryFile(options, generatedPath);
    if (!data) return null;

    const source: AssetMemorySizeSource = generatedPath.endsWith('.c') ? 'generated-c-array' : 'generated-output-file';
    if (!generatedPath.endsWith('.c')) {
        return {
            sizeBytes: data.byteLength,
            status: 'resolved',
            source
        };
    }

    const text: string = decodeAscii(data);
    const cArrayBytes: number | null = estimateGeneratedCArraySizeBytes(text);

    return {
        sizeBytes: cArrayBytes ?? data.byteLength,
        status: 'resolved',
        source,
        note: cArrayBytes === null ? 'Using generated C file byte length.' : undefined
    };
};

/**
 * Counts byte entries in generated C arrays that represent binary asset data.
 * Returns null when no matching byte array declaration is found.
 */
export const estimateGeneratedCArraySizeBytes = (source: string): number | null => {
    const arrayMatches: RegExpMatchArray[] = Array.from(
        source.matchAll(/(?:const\s+)?(?:unsigned\s+char|uint8_t|int8_t)[^=;]*=\s*\{([\s\S]*?)\};/g)
    );

    if (arrayMatches.length === 0) return null;

    return arrayMatches.reduce((sum: number, match: RegExpMatchArray): number => {
        const body: string = match[1]
            .replace(/\/\*[\s\S]*?\*\//g, '')
            .replace(/\/\/.*$/gm, '')
            .trim();

        if (!body) return sum;

        return (
            sum +
            body
                .split(',')
                .map((value: string) => value.trim())
                .filter(Boolean).length
        );
    }, 0);
};

/**
 * Reads a project-relative or absolute binary file through the caller-provided filesystem adapter.
 * Read errors are treated as unavailable files so analysis can continue with diagnostics/status notes.
 */
const readProjectBinaryFile = async (options: AssetMemoryEstimateOptions, path: string): Promise<Uint8Array | null> => {
    const filePath: string = resolveProjectFilePath(options.projectPath, path);

    try {
        const data: Uint8Array | null | undefined = await options.getBinaryFileData(filePath, { suppressError: true });
        return data ?? null;
    } catch {
        return null;
    }
};

/**
 * Decodes generated C source bytes without relying on TextDecoder availability in every runtime.
 * Generated C files are expected to be ASCII-compatible.
 */
const decodeAscii = (data: Uint8Array): string => {
    let result: string = '';
    for (const byte of data) result += String.fromCharCode(byte);
    return result;
};

/**
 * Resolves project-relative asset paths while preserving absolute paths and already-normalized project paths.
 */
const resolveProjectFilePath = (projectPath: string, filePath: string): string => {
    if (!filePath) return '';

    const normalizedFilePath: string = normalizePath(filePath);
    const normalizedProjectPath: string = normalizePath(projectPath);

    if (
        isAbsoluteFolderPath(filePath) ||
        normalizedFilePath.startsWith(`${normalizedProjectPath}/`) ||
        normalizedFilePath === normalizedProjectPath
    ) {
        return filePath;
    }

    return resolveToAbsolutePath(filePath.replace(/^[\\/]+/, ''), projectPath.replace(/[\\/]+$/, ''));
};

/**
 * Mirrors resource conversion output naming so the estimator can find generated files by convention.
 * Callers can override this when their export location differs.
 */
const getDefaultGeneratedAssetPath = (asset: AssetMemoryAssetReference): string | null => {
    if (asset.blockName === 'images' && asset.tagName === 'data') return `images/${asset.name}_data.c`;

    if (asset.blockName !== 'fonts') return null;

    const asFile: boolean = asset.attributes.as_file?.toLowerCase() === 'true';
    if (asset.tagName === 'bin') return asFile ? `fonts/${asset.name}.bin` : `fonts/${asset.name}_data.c`;

    if (asset.tagName === 'tiny_ttf' && !asFile) return `fonts/${fontSourcePathToCVarName(asset.sourcePath)}.c`;

    return null;
};

/**
 * Recreates the C variable/file stem used when tiny_ttf fonts are exported as byte arrays.
 */
const fontSourcePathToCVarName = (sourcePath: string): string =>
    basename(sourcePath)
        .replace(/[^a-zA-Z0-9_]/g, '_')
        .replace(/_+/g, '_') + '_data';
